/**
 * O que faz: centraliza a leitura e a escrita das regras de eventos Meta CAPI da clinica.
 * Onde usa: aba "Eventos CAPI" nas configuracoes da clinica.
 * Conexoes: Supabase client, React Query e Edge Function offline-conversions.
 * Tabelas: meta_conversion_event_rules, offline_conversion_imports e offline_conversion_rows.
 * Data: 08/07/2026
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// venda_registrada: Purchase de venda registrada com o lead fora da coluna de vendas (uma regra por clinica).
export type MetaConversionTriggerType =
  | 'kanban_stage'
  | 'phrase_sent'
  | 'offline_manual'
  | 'offline_import'
  | 'venda_registrada';
export type MetaConversionDedupeScope = 'lead_event_rule' | 'lead_event_day' | 'allow_repeat';
export type MetaConversionPhraseMatchType = 'equals' | 'contains';
export type MetaConversionDeliveryChannel = 'website' | 'whatsapp_ctwa' | 'both';

export interface MetaConversionEventRule {
  id: string;
  clinica_id: string;
  trigger_type: MetaConversionTriggerType;
  name: string;
  event_name: string;
  is_active: boolean;
  etapa_kanban_id: string | null;
  phrase_text: string | null;
  phrase_match_type: MetaConversionPhraseMatchType;
  require_value: boolean;
  default_value: number | null;
  default_currency: 'BRL';
  dedupe_scope: MetaConversionDedupeScope;
  delivery_channel: MetaConversionDeliveryChannel;
  created_at: string;
  updated_at: string;
}

export interface SaveMetaConversionRuleInput {
  id?: string;
  clinica_id: string;
  trigger_type: MetaConversionTriggerType;
  name: string;
  event_name: string;
  is_active?: boolean;
  etapa_kanban_id?: string | null;
  phrase_text?: string | null;
  phrase_match_type?: MetaConversionPhraseMatchType;
  require_value?: boolean;
  default_value?: number | null;
  default_currency?: 'BRL';
  dedupe_scope?: MetaConversionDedupeScope;
  delivery_channel?: MetaConversionDeliveryChannel;
}

export interface OfflineImportRowInput {
  leadId?: string;
  eventName: string;
  eventTime: string;
  value?: number;
  currency?: 'BRL';
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
}

interface SupabaseErrorLike {
  message?: string;
}

interface SupabaseListResult<T> {
  data: T[] | null;
  error: SupabaseErrorLike | null;
}

interface SupabaseSingleResult<T> {
  data: T | null;
  error: SupabaseErrorLike | null;
}

interface RulesSelectBuilder {
  select: (columns: string) => RulesSelectBuilder;
  eq: (column: string, value: string) => RulesSelectBuilder;
  order: (column: string, options: { ascending: boolean }) => RulesSelectBuilder;
  single: () => Promise<SupabaseSingleResult<MetaConversionEventRule>>;
  then: Promise<SupabaseListResult<MetaConversionEventRule>>['then'];
}

interface RulesInsertBuilder {
  select: (columns: string) => {
    single: () => Promise<SupabaseSingleResult<MetaConversionEventRule>>;
  };
}

interface RulesUpdateBuilder {
  eq: (column: string, value: string) => {
    select: (columns: string) => {
      single: () => Promise<SupabaseSingleResult<MetaConversionEventRule>>;
    };
  };
}

interface RulesDeleteBuilder {
  eq: (column: string, value: string) => Promise<{ error: SupabaseErrorLike | null }>;
}

const rulesTable = () =>
  (supabase.from('meta_conversion_event_rules' as never) as unknown as {
    select: (columns: string) => RulesSelectBuilder;
    insert: (payload: Record<string, unknown>) => RulesInsertBuilder;
    update: (payload: Record<string, unknown>) => RulesUpdateBuilder;
    delete: () => RulesDeleteBuilder;
  });

const sanitizeText = (value?: string | null) => {
  const text = String(value ?? '').trim();
  return text.length > 0 ? text : null;
};

const normalizeEventName = (value: string) => {
  const text = String(value ?? '').trim();
  return text.replace(/\s+/g, '_');
};

const readFunctionError = async (error: unknown) => {
  const response = (error as { context?: unknown })?.context;
  if (!(response instanceof Response)) return null;

  try {
    const payload = await response.clone().json();
    return typeof payload?.error === 'string' ? payload.error : null;
  } catch (_error) {
    return null;
  }
};

export const useMetaConversionEventRules = (clinicaId?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const queryKey = ['meta-conversion-event-rules', clinicaId];

  const rulesQuery = useQuery({
    queryKey,
    enabled: Boolean(clinicaId),
    queryFn: async () => {
      if (!clinicaId) return [];

      // Tabela: meta_conversion_event_rules
      // Operacao: SELECT
      // Campos lidos: configuracao de gatilho por etapa, frase e modo de deduplicacao.
      // Filtro: somente a clinica ativa, ordenando por tipo e criacao para manter a tela previsivel.
      const { data, error } = await rulesTable()
        .select('*')
        .eq('clinica_id', clinicaId)
        .order('trigger_type', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) {
        throw new Error(`Erro ao carregar regras de eventos: ${error.message}`);
      }

      return (data ?? []) as MetaConversionEventRule[];
    },
  });

  const invalidateRules = async () => {
    await queryClient.invalidateQueries({ queryKey });
  };

  const saveRule = useMutation({
    mutationFn: async (input: SaveMetaConversionRuleInput) => {
      const payload = {
        clinica_id: input.clinica_id,
        trigger_type: input.trigger_type,
        name: input.name.trim(),
        event_name: normalizeEventName(input.event_name),
        is_active: input.is_active ?? true,
        etapa_kanban_id: input.etapa_kanban_id ?? null,
        phrase_text: sanitizeText(input.phrase_text),
        phrase_match_type: input.phrase_match_type ?? 'equals',
        require_value: input.require_value ?? false,
        default_value: input.default_value ?? null,
        default_currency: input.default_currency ?? 'BRL',
        dedupe_scope: input.dedupe_scope ?? 'lead_event_rule',
        delivery_channel: input.delivery_channel ?? 'website',
      };

      if (input.id) {
        const { data, error } = await rulesTable()
          .update(payload)
          .eq('id', input.id)
          .select('*')
          .single();

        if (error) {
          const mensagem = error.message || 'Erro ao atualizar regra.';
          if (mensagem.includes('delivery_channel')) {
            throw new Error('O banco ainda não tem o campo Enviar para. Recarregue a página depois da atualização e salve de novo.');
          }
          throw new Error(`Erro ao atualizar regra: ${mensagem}`);
        }

        return data;
      }

      const { data, error } = await rulesTable()
        .insert(payload)
        .select('*')
        .single();

      if (error) {
        throw new Error(`Erro ao criar regra: ${error.message}`);
      }

      return data;
    },
    onSuccess: async () => {
      await invalidateRules();
      toast({
        title: 'Evento salvo',
        description: 'A configuração do evento foi atualizada.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao salvar regra',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteRule = useMutation({
    mutationFn: async (ruleId: string) => {
      const { error } = await rulesTable().delete().eq('id', ruleId);

      if (error) {
        throw new Error(`Erro ao remover regra: ${error.message}`);
      }
    },
    onSuccess: async () => {
      await invalidateRules();
      toast({
        title: 'Frase excluída',
        description: 'Ela não envia mais evento para a Meta.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao remover regra',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const importOfflineConversions = useMutation({
    mutationFn: async (payload: { clinicaId: string; sourceName: string; rows: OfflineImportRowInput[] }) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      const response = await supabase.functions.invoke('offline-conversions', {
        body: payload,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        const backendMessage = await readFunctionError(response.error);
        throw new Error(backendMessage || response.error.message || 'Erro ao importar conversões offline.');
      }

      if (response.data?.error) {
        throw new Error(String(response.data.error));
      }

      return response.data as { importId: string; queued: number; failed: number };
    },
    onSuccess: (data) => {
      toast({
        title: 'Importação concluída',
        description: `${data.queued} eventos na fila de envio e ${data.failed} linhas com falha.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro na importação offline',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    rulesQuery,
    saveRule,
    deleteRule,
    importOfflineConversions,
  };
};
